import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { Link } from 'react-router-dom';
import DataTable, { createTheme } from 'react-data-table-component';
import { Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';

import api from '../../services/api';
import ViewPaymentPlanModal from './ViewPaymentPlanModal';
import EditPaymentPlanModal from './EditPaymentPlanModal';
import DeleteConfirmationModal from '../DeleteConfirmationModal';

// 1. Create a custom theme (optional) to resemble old styling
createTheme('customTheme', {
    text: {
        primary: '#212529',
        secondary: '#495057',
    },
    background: {
        default: '#ffffff',
    },
    context: {
        background: '#e3f2fd',
        text: '#212529',
    },
    divider: {
        default: '#dee2e6',
    },
    action: {
        button: '#fff',
        hover: 'rgba(0,0,0,0.08)',
        disabled: 'rgba(0,0,0,0.12)',
    },
});

// 2. Custom styles: row height, fonts, etc.
const customStyles = {
    rows: {
        style: {
            minHeight: '50px', // row height
            fontSize: '15px',
        },
    },
    headCells: {
        style: {
            backgroundColor: '#f8f9fa',
            fontWeight: '600',
            fontSize: '1rem',
        },
    },
    cells: {
        style: {
            fontSize: '0.95rem',
        },
    },
};

const PaymentPlansTable = () => {
    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    // View/Edit modals
    const [showViewModal, setShowViewModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [currentPaymentPlan, setCurrentPaymentPlan] = useState(null);

    // Delete Confirmation modal
    const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState(false);
    const [deletedPaymentPlan, setDeletedPaymentPlan] = useState(null);

    // Fetch data on mount
    const fetchData = async () => {
        try {
            setIsLoading(true);
            const response = await api.get('/paymentplans/');
            if (response.data?.response?.data) {
                setData(response.data.response.data);
            } else {
                setData([]);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to fetch payment plans');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // ----- Delete -----
    const handleDelete = async () => {
        if (!deletedPaymentPlan) return;
        try {
            setIsLoading(true);
            const response = await api.delete(`/paymentplans/${deletedPaymentPlan.id}`);
            const resData = response.data;

            if (resData.statusCode === 200) {
                // Some APIs might return 200 for "request submitted"
                toast.warning('Deletion request submitted, awaiting approval.');
            } else if (resData.statusCode === 201) {
                toast.success('Payment plan deleted successfully.');
                fetchData();
            } else {
                toast.error('Failed to delete payment plan. Unexpected response.');
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error deleting payment plan');
        } finally {
            setIsLoading(false);
            setShowDeleteConfirmationModal(false);
        }
    };

    // ----- Edit -----
    const handleSaveEdit = async (updatedPaymentPlan) => {
        try {
            setIsLoading(true);
            const response = await api.put(`/paymentplans/${updatedPaymentPlan.id}`, updatedPaymentPlan);

            if (response.data.statusCode === 200) {
                toast.success('Payment plan updated successfully.');
                fetchData();
            } else {
                toast.error('Failed to update payment plan. Unexpected response.');
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error updating payment plan');
        } finally {
            setIsLoading(false);
            setShowEditModal(false);
        }
    };

    // ----- Define table columns -----
    const columns = [
        {
            name: 'Nickname',
            selector: (row) => row.name,
            sortable: true,
        },
        {
            name: 'Installments',
            selector: (row) => `${row.installments} yrs`,
            sortable: true,
        },
        {
            name: 'Downpayment',
            selector: (row) => `${row.downpayment} %`,
            sortable: true,
        },
        {
            name: 'Actions',
            cell: (row) => (
                <div>
                    {/* View */}
                    <button
                        onClick={() => {
                            setCurrentPaymentPlan(row);
                            setShowViewModal(true);
                        }}
                        className="w-32-px h-32-px me-8 bg-primary-light text-primary-600 rounded-circle d-inline-flex align-items-center justify-content-center"
                    >
                        <Icon icon="iconamoon:eye-light" />
                    </button>
                    {/* Edit */}
                    <button
                        onClick={() => {
                            setCurrentPaymentPlan(row);
                            setShowEditModal(true);
                        }}
                        className="w-32-px h-32-px me-8 bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center"
                    >
                        <Icon icon="lucide:edit" />
                    </button>
                    {/* Delete
                    <button
                        onClick={() => {
                            setDeletedPaymentPlan(row);
                            setShowDeleteConfirmationModal(true);
                        }}
                        className="w-32-px h-32-px me-8 bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                    >
                        <Icon icon="mingcute:delete-2-line" />
                    </button> */}
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            width: '150px',
        },
    ];

    return (
        <div className="card basic-data-table px-3 py-3">
            <div className="d-flex justify-content-end mb-3">
                <Link
                    to="/add-paymentplan"
                    className="btn btn-primary btn-sm d-flex align-items-center gap-2"
                >
                    <Icon icon="ic:baseline-plus" className="icon text-sm" />
                    Add New Payment Plan
                </Link>
            </div>

            {/* Data Table */}
            <DataTable
                columns={columns}
                data={data}
                theme="customTheme"
                customStyles={customStyles}
                progressPending={isLoading}
                pagination
                highlightOnHover
                responsive
                striped
                noDataComponent="No results"
            />

            {/* View Modal */}
            <ViewPaymentPlanModal
                show={showViewModal}
                handleClose={() => setShowViewModal(false)}
                paymentplan={currentPaymentPlan}
            />

            {/* Edit Modal */}
            <EditPaymentPlanModal
                show={showEditModal}
                handleClose={() => setShowEditModal(false)}
                paymentplan={currentPaymentPlan}
                handleSave={handleSaveEdit}
            />

            {/* Delete Confirmation Modal */}
            <DeleteConfirmationModal
                show={showDeleteConfirmationModal}
                handleClose={() => setShowDeleteConfirmationModal(false)}
                handleDelete={handleDelete}
                content={
                    <>
                        <Modal.Header>
                            <Modal.Title>Delete Payment Plan</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <p>Are you sure you want to delete this Payment Plan?</p>
                        </Modal.Body>
                    </>
                }
            />
        </div>
    );
};

export default PaymentPlansTable;
